import {
  LineItemEntityType,
  PLAN_LINE_ENTITY_TYPES,
  ItemType,
} from "@/utils/item-type-constants"

const PLAN_ENTITY_TYPES = PLAN_LINE_ENTITY_TYPES;
const ADDON_ENTITY_TYPES = new Set([
  LineItemEntityType.ADDON,
  LineItemEntityType.ADDON_ITEM_PRICE,
  LineItemEntityType.CHARGE_ITEM_PRICE,
]);
const COUPON_ENTITY_TYPES = new Set(['item_level_coupon', 'document_level_coupon']);
const MANUAL_DISCOUNT_ENTITY_TYPES = new Set(['item_level_discount', 'document_level_discount']);

function normalizeEstimateRoot(response) {
  return response?.estimate || response || {};
}

function lowerEnum(value) {
  return typeof value === 'string' ? value.toLowerCase() : value;
}

function isItemsModelSubscription(rootState, subscriptionId) {
  const subscription = rootState?.customer?.subscriptions
    ?.find((s) => s.id === subscriptionId);
  return !!subscription?.is_items_model;
}

export function shouldUsePublicEstimateApi({ getters, rootState, isPortal = false }, subscriptionId) {
  // Public items estimate is portal edit-subscription only.
  if (!isPortal) {
    return false;
  }
  if (!getters?.is_mps_enabled) {
    return false;
  }
  if (getters.is_items_model) {
    return true;
  }
  return isItemsModelSubscription(rootState, subscriptionId);
}

// Catalog details from invoice discount; never use generic "Coupon applied" as name.
function buildDetailsFromInvoiceDiscount(discount) {
  const discountType = lowerEnum(discount && discount.discount_type);
  if (!discountType) {
    return undefined;
  }
  const normalizedType = (discountType === 'flat') ? 'fixed_amount' : discountType;
  const details = { discount_type: normalizedType };
  if (normalizedType === 'fixed_amount' && discount.amount != null) {
    details.discount_amount = discount.amount;
  }
  if (normalizedType === 'percentage' && discount.discount_percentage != null) {
    details.discount_percentage = discount.discount_percentage;
  } else if (normalizedType === 'percentage' && discount.percentage != null) {
    details.discount_percentage = discount.percentage;
  }
  return details;
}

function mapDiscounts(discounts, entityTypes, defaultType, orderItem) {
  return (discounts || [])
    .filter((discount) => entityTypes.has(discount.entity_type))
    .map((discount) => {
      const id = discount.entity_id || discount.id;
      const code = discount.coupon_set_code || discount.entity_id || discount.code || id;
      const cartDetails = findCartCouponDetails(orderItem, id)
        || findCartCouponDetails(orderItem, code);
      return {
        id,
        code,
        amount: discount.amount,
        description: discount.description,
        details: cartDetails || buildDetailsFromInvoiceDiscount(discount),
        type: defaultType,
      };
    })
    .filter((coupon) => !!coupon.id);
}


function buildOrderEntity(cartEntity, subscriptionItem, lineItem, resolvedTiers) {
  if (!cartEntity) {
    return null;
  }

  const entity = {
    id: cartEntity.id,
    type: cartEntity.type || ItemType.ADDON,
    details: cartEntity.details,
    charge_type: cartEntity.charge_type,
    added_type: cartEntity.added_type,
    metered: cartEntity.metered,
    quantity: cartEntity.quantity,
    quantity_in_decimal: cartEntity.quantity_in_decimal,
    unit_amount: cartEntity.unit_amount,
    amount: cartEntity.amount,
    unit_amount_in_decimal: cartEntity.unit_amount_in_decimal,
    amount_in_decimal: cartEntity.amount_in_decimal,
    // Prefer existing catalog details over estimate enrichment.
    pricing_model: cartEntity.pricing_model
      || (cartEntity.details && cartEntity.details.pricing_model),
    period: cartEntity.period,
    period_unit: cartEntity.period_unit,
    tiers: cartEntity.tiers ? [...cartEntity.tiers] : undefined,
  };

  // subscription_items fill metadata; do not overwrite invoice amounts (proration).
  if (subscriptionItem) {
    if (subscriptionItem.billing_period != null) {
      entity.period = subscriptionItem.billing_period;
    }
    if (subscriptionItem.billing_period_unit) {
      // Normalize period_unit casing from public API.
      entity.period_unit = lowerEnum(subscriptionItem.billing_period_unit);
    }
    if (subscriptionItem.unit_price != null) {
      entity.unit_amount = subscriptionItem.unit_price;
    }
    if (subscriptionItem.unit_price_in_decimal != null) {
      entity.unit_amount_in_decimal = subscriptionItem.unit_price_in_decimal;
    }
    if (subscriptionItem.amount != null) {
      entity.amount = subscriptionItem.amount;
    }
    if (subscriptionItem.amount_in_decimal != null) {
      entity.amount_in_decimal = subscriptionItem.amount_in_decimal;
    }
    if (subscriptionItem.pricing_model && !entity.pricing_model) {
      entity.pricing_model = lowerEnum(subscriptionItem.pricing_model);
    }
    if (subscriptionItem.free_quantity != null) {
      entity.free_quantity = subscriptionItem.free_quantity;
    }
    if (!entity.added_type && subscriptionItem.added_type) {
      entity.added_type = lowerEnum(subscriptionItem.added_type);
    }
    const subscriptionItemDescription = subscriptionItem.description
      || subscriptionItem.item_description
      || subscriptionItem.entity_description;
    if (subscriptionItemDescription) {
      entity.details = {
        ...(entity.details || {}),
        description: subscriptionItemDescription,
      };
    }
    if (!entity.details && (subscriptionItem.external_name || subscriptionItem.name)) {
      entity.details = { name: subscriptionItem.external_name || subscriptionItem.name };
    }

    if (subscriptionItem.metered) {
      entity.metered = true;
    } else if (subscriptionItem.quantity_in_decimal != null) {
      entity.quantity_in_decimal = subscriptionItem.quantity_in_decimal;
      delete entity.quantity;
    } else if (subscriptionItem.quantity != null) {
      entity.quantity = subscriptionItem.quantity;
    }
  }

  // Invoice line items win for amounts/qty/pricing_model when present.
  if (lineItem) {
    if (lineItem.quantity != null) {
      entity.quantity = lineItem.quantity;
      delete entity.quantity_in_decimal;
    }
    if (lineItem.quantity_in_decimal != null) {
      entity.quantity_in_decimal = lineItem.quantity_in_decimal;
      delete entity.quantity;
    }
    if (lineItem.unit_amount != null) entity.unit_amount = lineItem.unit_amount;
    if (lineItem.amount != null) entity.amount = lineItem.amount;
    if (lineItem.unit_amount_in_decimal != null) {
      entity.unit_amount_in_decimal = lineItem.unit_amount_in_decimal;
    }
    if (lineItem.amount_in_decimal != null) {
      entity.amount_in_decimal = lineItem.amount_in_decimal;
    }
    if (lineItem.pricing_model) {
      entity.pricing_model = lineItem.pricing_model.toLowerCase();
    }
  }

  // Metered items never carry quantity.
  if (entity.metered) {
    delete entity.quantity;
    delete entity.quantity_in_decimal;
  }

  if (lineItem?.entity_type === LineItemEntityType.CHARGE_ITEM_PRICE) {
    entity.charge_type = entity.charge_type || 'non_recurring';
  }
  if (entity.type === ItemType.ADDON && !entity.added_type) {
    entity.added_type = 'manual';
  }

  // Tiers come from invoice line items only (immediate, then next invoices).
  if (resolvedTiers && resolvedTiers.length) {
    entity.tiers = resolvedTiers;
  }

  return entity;
}

// Resolve tiers from the same invoice as the matching line item.
function resolveEntityTiers(entityId, invoices) {
  for (const invoice of (invoices || [])) {
    if (!invoice) {
      continue;
    }
    const lineItem = (invoice.line_items || []).find((item) => item.entity_id === entityId);
    if (!lineItem) {
      continue;
    }
    const tiers = (invoice.line_item_tiers || []).filter((tier) => tier.line_item_id === lineItem.id);
    if (tiers.length) {
      return tiers;
    }
  }
  return [];
}

function findCartCoupon(orderItem, couponId) {
  if (!couponId) {
    return undefined;
  }
  // Match coupon by id or code.
  return (orderItem?.coupon_list || []).find(
    (coupon) => coupon.id === couponId || coupon.code === couponId
  );
}

function findCartCouponDetails(orderItem, couponId) {
  return findCartCoupon(orderItem, couponId)?.details;
}

function buildOrderCoupons(orderItem, invoiceDiscounts) {
  return mergeCouponMaps(
    buildCartCouponMap(orderItem, ItemType.COUPON),
    mapDiscounts(invoiceDiscounts, COUPON_ENTITY_TYPES, ItemType.COUPON, orderItem)
  );
}

function buildOrderManualDiscounts(orderItem, invoiceDiscounts) {
  return mergeCouponMaps(
    buildCartCouponMap(orderItem, 'manual_discount'),
    mapDiscounts(invoiceDiscounts, MANUAL_DISCOUNT_ENTITY_TYPES, 'manual_discount', orderItem)
  );
}

function buildCartCouponMap(orderItem, type) {
  const byId = new Map();
  (orderItem?.coupon_list || [])
    .filter((coupon) => {
      if (type === ItemType.COUPON) {
        return !coupon.type || coupon.type === ItemType.COUPON;
      }
      return coupon.type === type;
    })
    .forEach((coupon) => {
      const id = coupon.id || coupon.code;
      if (!id || byId.has(id)) {
        return;
      }
      byId.set(id, {
        id,
        code: coupon.code || coupon.id,
        amount: coupon.amount,
        description: coupon.description,
        details: coupon.details,
        type,
      });
    });
  return byId;
}

function findMappedCoupon(couponsById, invoiceCoupon) {
  if (!invoiceCoupon || !invoiceCoupon.id) {
    return undefined;
  }
  if (couponsById.has(invoiceCoupon.id)) {
    return { key: invoiceCoupon.id, value: couponsById.get(invoiceCoupon.id) };
  }
  if (invoiceCoupon.code && couponsById.has(invoiceCoupon.code)) {
    return { key: invoiceCoupon.code, value: couponsById.get(invoiceCoupon.code) };
  }
  for (const [key, value] of couponsById) {
    if (
      value.code === invoiceCoupon.id
      || value.id === invoiceCoupon.code
      || (invoiceCoupon.code && value.code === invoiceCoupon.code)
    ) {
      return { key, value };
    }
  }
  return undefined;
}

function mergeCouponMaps(cartCouponsById, invoiceCoupons) {
  const byId = new Map(cartCouponsById);

  (invoiceCoupons || []).forEach((invoiceCoupon) => {
    if (!invoiceCoupon.id) {
      return;
    }
    const matched = findMappedCoupon(byId, invoiceCoupon);
    if (!matched) {
      byId.set(invoiceCoupon.id, invoiceCoupon);
      return;
    }
    // Prefer cart details; re-key to invoice entity_id for stable merges.
    if (matched.key !== invoiceCoupon.id) {
      byId.delete(matched.key);
    }
    byId.set(invoiceCoupon.id, {
      ...matched.value,
      ...invoiceCoupon,
      code: matched.value.code || invoiceCoupon.code,
      details: matched.value.details || invoiceCoupon.details,
      type: matched.value.type || invoiceCoupon.type,
      amount: invoiceCoupon.amount != null ? invoiceCoupon.amount : matched.value.amount,
      description: invoiceCoupon.description != null
        ? invoiceCoupon.description
        : matched.value.description,
    });
  });

  return Array.from(byId.values());
}

function buildOrder(orderItem, subscriptionEstimate, invoiceEstimate, tierInvoices = []) {
  const subscriptionItems = subscriptionEstimate?.subscription_items || [];
  const subscriptionItemsById = new Map(
    subscriptionItems.map((item) => [item.item_price_id, item])
  );
  const lineItems = invoiceEstimate?.line_items || [];
  const lineItemsByEntityId = new Map(lineItems.map((item) => [item.entity_id, item]));

  const order = {
    addons: [],
    event_based_addons: orderItem?.event_based_addons
      ? [...orderItem.event_based_addons]
      : [],
  };

  if (orderItem?.plan) {
    order.plan = buildOrderEntity(
      orderItem.plan,
      subscriptionItemsById.get(orderItem.plan.id),
      lineItemsByEntityId.get(orderItem.plan.id),
      resolveEntityTiers(orderItem.plan.id, tierInvoices)
    );
  }

  // Re-price additional plans from the estimate like the primary plan.
  order.additionalPlans = (orderItem?.additionalPlans || []).map((plan) => buildOrderEntity(
    plan,
    subscriptionItemsById.get(plan.id),
    lineItemsByEntityId.get(plan.id),
    resolveEntityTiers(plan.id, tierInvoices)
  )).filter(Boolean);

  order.addons = (orderItem?.addons || []).map((addon) => buildOrderEntity(
    addon,
    subscriptionItemsById.get(addon.id),
    lineItemsByEntityId.get(addon.id),
    resolveEntityTiers(addon.id, tierInvoices)
  )).filter(Boolean);

  order.coupons = buildOrderCoupons(orderItem, invoiceEstimate?.discounts);
  order.manual_discounts = buildOrderManualDiscounts(orderItem, invoiceEstimate?.discounts);

  return order;
}

function mergeOrderCoupons(order, discounts, orderItem) {
  if (!discounts?.length) {
    return;
  }
  const existingById = new Map(
    (order.coupons || []).map((coupon) => [coupon.id || coupon.code, coupon])
  );
  order.coupons = mergeCouponMaps(
    existingById,
    mapDiscounts(discounts, COUPON_ENTITY_TYPES, ItemType.COUPON, orderItem)
  );
}

function mergeOrderManualDiscounts(order, discounts, orderItem) {
  if (!discounts?.length) {
    return;
  }
  const existingById = new Map(
    (order.manual_discounts || []).map((discount) => [discount.id || discount.code, discount])
  );
  order.manual_discounts = mergeCouponMaps(
    existingById,
    mapDiscounts(discounts, MANUAL_DISCOUNT_ENTITY_TYPES, 'manual_discount', orderItem)
  );
}

// Charge-now invoice only — ignore singular next_invoice_estimate under MPS.
function resolveImmediateInvoice(estimate) {
  let immediateInvoice = estimate.invoice_estimate || estimate.immediate_invoice_estimate;

  if (!immediateInvoice && estimate.invoice_estimates?.length) {
    [immediateInvoice] = estimate.invoice_estimates;
  }

  return immediateInvoice;
}

// Renewal invoices: next_invoice_estimates[] only (no singular fallback).
function resolveNextInvoiceEstimates(estimate) {
  return estimate.next_invoice_estimates ? [...estimate.next_invoice_estimates] : [];
}

export function transformPublicEstimateToCartResponse(response, orderItem) {
  const estimate = normalizeEstimateRoot(response);
  const immediateInvoice = resolveImmediateInvoice(estimate);
  const nextInvoiceEstimates = resolveNextInvoiceEstimates(estimate);

  const cartResponse = {
    subscription_estimate: estimate.subscription_estimate,
    // Never promote next_invoice_estimate into the immediate invoice.
    immediate_invoice_estimate: immediateInvoice,
    credit_note_estimates: estimate.credit_note_estimates || [],
    contract_term: estimate.contract_term,
    next_invoice_estimates: nextInvoiceEstimates,
  };

  // Prefer immediate invoice amounts; else first next invoice (no fake immediate).
  const orderInvoice = immediateInvoice || nextInvoiceEstimates[0];

  // Tier priority: immediate invoice, then next invoices.
  const tierInvoices = [orderInvoice, ...nextInvoiceEstimates]
    .filter((invoice, index, all) => invoice && all.indexOf(invoice) === index);

  cartResponse.order = buildOrder(
    orderItem,
    estimate.subscription_estimate,
    orderInvoice,
    tierInvoices
  );

  for (const nextEstimate of nextInvoiceEstimates) {
    mergeOrderCoupons(cartResponse.order, nextEstimate.discounts, orderItem);
    mergeOrderManualDiscounts(cartResponse.order, nextEstimate.discounts, orderItem);
  }

  return cartResponse;
}

const PUBLIC_ESTIMATE_PARAM_PATTERNS = [
  /^subscription\[id\]$/,
  /^subscription_items\[item_price_id\]\[\d+\]$/,
  /^subscription_items\[quantity\]\[\d+\]$/,
  /^subscription_items\[quantity_in_decimal\]\[\d+\]$/,
  /^subscription_items\[unit_price\]\[\d+\]$/,
  /^subscription_items\[unit_price_in_decimal\]\[\d+\]$/,
  /^item_tiers\[[^\]]+\]\[\d+\]$/,
  /^coupon_ids\[\d+\]$/,
  /^replace_coupon_list$/,
  /^replace_items_list$/,
  /^end_of_term$/,
  /^prorate$/,
  /^invoice_immediately$/,
  // Omit use_existing_balances / include_delayed_charges to match cart estimate.
  /^change_option$/,
  /^billing_cycles$/,
  /^billing_address\[[^\]]+\]$/,
  /^shipping_address\[[^\]]+\]$/,
  /^customer\[vat_number\]$/,
  /^customer\[vat_number_prefix\]$/,
  /^customer\[registered_for_gst\]$/,
  /^locale$/,
];

function isSupportedPublicEstimateParam(key) {
  return PUBLIC_ESTIMATE_PARAM_PATTERNS.some((pattern) => pattern.test(key));
}

export function sanitizePublicEstimateRequest(params) {
  return Object.keys(params).reduce((output, key) => {
    if (isSupportedPublicEstimateParam(key)) {
      output[key] = params[key];
    }
    return output;
  }, {});
}

export function fetchUpdateSubscriptionEstimate(apiClient, {
  params,
  subscriptionId,
  hpToken,
  orderItem,
  usePublicEstimateApi,
}) {
  if (usePublicEstimateApi) {
    const sanitizedParams = sanitizePublicEstimateRequest(params);
    return apiClient.estimates.update_subscription_for_items(
      { hp_token: hpToken },
      sanitizedParams
    ).then((data) => transformPublicEstimateToCartResponse(data, orderItem));
  }

  return apiClient.cart.update_subscription(
    { id: subscriptionId, hp_token: hpToken },
    params
  );
}
